import React from "react";
import { motion } from "framer-motion";
import {
  ReceiptText,
  ShieldCheck,
  Building2,
  BookOpen,
  ArrowUpRight,
} from "lucide-react";

const services = [
  {
    icon: ReceiptText,
    title: "Tax Filing",
    sub: "Income Tax & GST",
    points: [
      "ITR for individuals, firms & companies",
      "GST registration, returns & reconciliations",
      "TDS compliance and refunds",
      "Notice & assessment representation",
    ],
    testid: "service-card-tax",
  },
  {
    icon: ShieldCheck,
    title: "Auditing & Assurance",
    sub: "Statutory · Internal · Tax",
    points: [
      "Statutory audit under Companies Act",
      "Tax audit u/s 44AB of the IT Act",
      "Internal & management audits",
      "Stock audit and bank concurrent audits",
    ],
    testid: "service-card-audit",
  },
  {
    icon: Building2,
    title: "Company Registration",
    sub: "Incorporation & ROC",
    points: [
      "Private Limited, LLP & OPC incorporation",
      "Annual ROC filings & secretarial work",
      "MoA / AoA drafting and amendments",
      "Director appointments & DIN/DSC",
    ],
    testid: "service-card-registration",
  },
  {
    icon: BookOpen,
    title: "Bookkeeping & Accounting",
    sub: "Outsourced Finance Function",
    points: [
      "Monthly bookkeeping in Tally / Zoho",
      "Bank reconciliation & ledger scrutiny",
      "Financial statements & MIS reports",
      "Payroll processing & TDS on salaries",
    ],
    testid: "service-card-bookkeeping",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 bg-[#F5F1E8]/50"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <div className="overline mb-5">
              <span className="divider-gold mr-3" /> What we do
            </div>
            <h2
              className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#111814] max-w-3xl"
              data-testid="services-heading"
            >
              Four disciplines, one standard{" "}
              <em className="italic text-[#1A2F24]">of care.</em>
            </h2>
          </div>
          <p className="text-[#3A453F] text-base md:text-lg max-w-md leading-relaxed">
            Whether you are filing your first return or steering a growing
            company through audit season, every engagement is led personally by
            a qualified Chartered Accountant.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="service-card p-8 md:p-10 flex flex-col"
                data-testid={s.testid}
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="w-14 h-14 border border-[#1A2F24] flex items-center justify-center text-[#1A2F24]">
                    <Icon size={22} strokeWidth={1.4} />
                  </div>
                  <span className="overline">0{i + 1}</span>
                </div>

                <div className="overline mb-3">{s.sub}</div>
                <h3 className="font-serif text-3xl md:text-4xl text-[#111814] leading-tight mb-6">
                  {s.title}
                </h3>

                <ul className="space-y-3 mb-8">
                  {s.points.map((p) => (
                    <li
                      key={p}
                      className="flex items-start gap-3 text-[#3A453F] text-[15px] leading-relaxed"
                    >
                      <span className="mt-2 w-1.5 h-1.5 bg-[#C5A059] shrink-0" />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="#contact"
                  className="mt-auto inline-flex items-center gap-2 text-[#1A2F24] text-sm tracking-[0.14em] uppercase font-medium group"
                  data-testid={`${s.testid}-cta`}
                >
                  Enquire
                  <ArrowUpRight
                    size={16}
                    className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                  />
                </a>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
